<script>
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useAutomationLocales } from 'dashboard/routes/dashboard/settings/wgptAutomations/composables/useAutomationLocales';
import ComboBox from 'next/combobox/ComboBox.vue';
import IconNext from 'next/icon/Icon.vue';

export default {
  components: {
    ComboBox,
    IconNext,
  },
  props: {
    value: {
      type: [String, Number, Date, Boolean, Object, Array],
      default: undefined,
    },
    defaultValue: {
      type: [String, Number, Date, Boolean, Object, Array],
      default: undefined,
    },
    label: {
      type: String,
      default: '',
    },
    fallbackLabel: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      required: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    dirty: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['change'],
  setup() {
    const { toLocale } = useAutomationLocales();

    return {
      v$: useVuelidate(),
      toLocale,
    };
  },
  data() {
    return {
      updatedValue: this.toBooleanString(this.value ?? this.defaultValue),
      options: [
        {
          label: this.$t(
            'WGPT_AUTOMATIONS.EDITOR.SIDEBAR.DYNAMIC_FIELDS.SELECT.BOOLEAN_TRUE_OPTION'
          ),
          value: 'true',
        },
        {
          label: this.$t(
            'WGPT_AUTOMATIONS.EDITOR.SIDEBAR.DYNAMIC_FIELDS.SELECT.BOOLEAN_FALSE_OPTION'
          ),
          value: 'false',
        },
      ],
    };
  },
  computed: {
    defaultOptionLabel() {
      if (this.placeholder) return this.toLocale(this.placeholder);
      if (this.required)
        return this.$t(
          'WGPT_AUTOMATIONS.EDITOR.SIDEBAR.DYNAMIC_FIELDS.SELECT.DEFAULT_OPTION_REQUIRED'
        );
      return this.$t(
        'WGPT_AUTOMATIONS.EDITOR.SIDEBAR.DYNAMIC_FIELDS.SELECT.DEFAULT_OPTION'
      );
    },
    descriptionTooltip() {
      if (!this.description) return null;

      return {
        content: this.toLocale(this.description),
        popperClass:
          'max-w-[300px] max-h-[300px] overflow-y-auto whitespace-pre-line',
        boundary: 'body',
        popperTriggers: ['hover'],
      };
    },
  },
  validations() {
    return {
      updatedValue: {
        required: requiredIf(this.required),
      },
    };
  },
  watch: {
    value(value) {
      this.updatedValue = this.toBooleanString(value);
    },
  },
  mounted() {
    if (this.dirty) {
      this.$nextTick(() => {
        this.v$.$validate();
      });
    }
  },
  methods: {
    toBooleanString(value) {
      if (value === true || value === 'true') return 'true';
      if (value === false || value === 'false') return 'false';
      return '';
    },
    updateValue(rawValue) {
      this.updatedValue = rawValue;
      this.$emit('change', this.formatSelectValue(rawValue));
    },
    formatSelectValue(value) {
      if (value === '') return null;

      return value === 'true';
    },
  },
};
</script>

<template>
  <div
    class="relative flex flex-col min-w-0 gap-1"
    :class="{ error: v$.updatedValue.$error }"
  >
    <label
      v-if="label || descriptionTooltip"
      class="flex items-center gap-2 text-sm font-medium text-n-slate-12"
    >
      <div v-if="label" class="block truncate">
        {{ toLocale(label, fallbackLabel) }}<span v-if="required">*</span>
      </div>
      <IconNext
        v-if="descriptionTooltip"
        v-tooltip.top="descriptionTooltip"
        icon="i-lucide-info"
        class="text-n-slate-11 size-3.5"
      />
    </label>

    <ComboBox
      v-model="updatedValue"
      :disabled="disabled"
      :options="options"
      :placeholder="defaultOptionLabel"
      :has-error="v$.updatedValue.$error"
      class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      :class="{
        '[&>div>button]:border [&>div>button]:border-n-ruby-8 [&>div>button:not(.focused)]:hover:!border-n-ruby-9':
          v$.updatedValue.$error,
      }"
      @update:model-value="updateValue"
    />

    <p
      v-if="v$.updatedValue.$error"
      class="min-w-0 mt-1 mb-0 text-xs truncate transition-all duration-500 ease-in-out text-n-ruby-9 dark:text-n-ruby-9"
    >
      <template v-if="v$.updatedValue.required.$invalid">
        {{ $t('WGPT_AUTOMATIONS.REGISTRY.FORM.ERRORS.REQUIRED') }}
      </template>
      <template v-else>
        {{ $t('WGPT_AUTOMATIONS.REGISTRY.FORM.ERRORS.INVALID') }}
      </template>
    </p>
  </div>
</template>

<style scoped>
.placeholder {
  @apply text-slate-400 dark:text-slate-400;
}
</style>
